import React, { useEffect, useState } from 'react';

import { FIELD_LABEL, FIELD_SELECT } from './helpers/configFieldStyles';
import SaveConfigModal from './SaveConfigModal';

import { apiClient } from 'Helpers/network';

import IconButton from 'CommonComponents/IconButton';
import SimpleConfirmationModal from 'CommonComponents/SimpleConfirmationModal';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

import TrashIcon from 'Assets/icons/trash.svg';

/**
 * "Saved configurations" bar for the Actuals-vs-Budget config screen (IB-834).
 *
 * Personal-only saved set-ups: a dropdown to load one, a button to save the
 * current screen, and a delete affordance for the selected one. It owns the
 * list + save/delete network calls; snapshotting and restoring the actual form
 * controls is delegated to the parent via `buildSnapshot` / `onApply` (only the
 * parent can reach every control — Formik values, context setters, and the
 * local scope selection).
 */
export default function SavedConfigBar({ indexUrl, storeUrl, deleteUrlTemplate, buildSnapshot, onApply }) {
    const [savedConfigs, setSavedConfigs] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [saving, setSaving] = useState(false);

    const toast = useToast();
    const saveModal = useModal();
    const deleteModal = useModal();

    useEffect(() => {
        if (!indexUrl) {
            return;
        }

        apiClient
            .get(indexUrl)
            .then(({ data }) => setSavedConfigs(Array.isArray(data?.data) ? data.data : []))
            .catch((error) => console.log(error));
    }, [indexUrl]);

    const selectedConfig = savedConfigs.find((config) => String(config.id) === String(selectedId));

    const handleSelect = (event) => {
        const id = event.target.value;
        setSelectedId(id);

        const config = savedConfigs.find((item) => String(item.id) === String(id));
        if (config) {
            onApply(config.parameters || {});
        }
    };

    const handleSave = (name) => {
        if (!storeUrl) {
            return Promise.resolve();
        }

        setSaving(true);

        return apiClient
            .post(storeUrl, { name, parameters: buildSnapshot() })
            .then(({ data }) => {
                setSavedConfigs((prev) => [data, ...prev.filter((config) => config.id !== data.id)]);
                setSelectedId(String(data.id));
                toast.success($t('Configuration saved.'));
                saveModal.hide();
            })
            .catch(() => {
                toast.error($t('The configuration could not be saved. Please try again.'));
            })
            .finally(() => setSaving(false));
    };

    const handleDelete = () => {
        if (!selectedConfig || !deleteUrlTemplate) {
            return;
        }

        const url = deleteUrlTemplate.replace('REPORT_FILTER_SET_ID', selectedConfig.id);

        apiClient
            .delete(url)
            .then(() => {
                setSavedConfigs((prev) => prev.filter((config) => String(config.id) !== String(selectedConfig.id)));
                setSelectedId('');
                toast.success($t('Configuration deleted.'));
            })
            .catch(() => {
                toast.error($t('The configuration could not be deleted. Please try again.'));
            })
            .finally(() => deleteModal.hide());
    };

    return (
        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
            <div className="tw-w-[320px]">
                <label className={FIELD_LABEL} htmlFor="saved-configuration">
                    {$t('Saved configuration')}
                </label>
                <select id="saved-configuration" className={FIELD_SELECT} value={selectedId} onChange={handleSelect} disabled={savedConfigs.length === 0}>
                    <option value="">{savedConfigs.length === 0 ? $t('No saved configurations') : $t('Load a saved configuration')}</option>
                    {savedConfigs.map((config) => (
                        <option key={config.id} value={config.id}>
                            {config.name}
                        </option>
                    ))}
                </select>
            </div>

            <button
                type="button"
                className="tw-rounded-full tw-border tw-border-blue tw-bg-white tw-px-[18px] tw-py-[9px] tw-text-[13.5px] tw-font-medium tw-text-blue hover:tw-bg-blue-10"
                onClick={() => saveModal.show()}
            >
                {$t('Save configuration')}
            </button>

            <IconButton
                variant="danger"
                ghost
                aria-label={$t('Delete configuration')}
                title={$t('Delete configuration')}
                disabled={!selectedConfig}
                onClick={() => deleteModal.show()}
            >
                <TrashIcon className="tw-w-4 tw-h-4" />
            </IconButton>

            <SaveConfigModal modal={saveModal} onSave={handleSave} saving={saving} />

            <SimpleConfirmationModal
                allowAction={true}
                modalId="delete-avb-config-modal"
                modalRef={deleteModal.ref}
                modalElement={deleteModal.element}
                title={$t('Delete configuration')}
                confirmationMsg={selectedConfig ? $t('Are you sure you want to delete "{{name}}"?', { name: selectedConfig.name }) : ''}
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Delete')}
                closeHandler={() => deleteModal.hide()}
                successHandler={handleDelete}
            />
        </div>
    );
}
